import { useForm, getFormProps, getInputProps } from '@conform-to/react'
import { parseWithZod, getZodConstraint } from '@conform-to/zod'
import * as E from '@react-email/components'
import { Link, Form, useSearchParams, useActionData } from '@remix-run/react'
import { LuMail } from 'react-icons/lu'
import { SiInstagram, SiLinkedin } from 'react-icons/si'
import { z } from 'zod'
import { SOCIALS } from '#app/config/constants'
import nix18 from '#app/images/NIX18_pms.png'
import { EmailSchema, NameSchema } from '#app/utils/user-validation'
import IconLink from './iconLink'
import { H4, Paragraph } from './typography'

const LINKS = [
	{ name: 'Home', to: '/' },
	{ name: 'Margaret', to: '/about-margaret' },
	{ name: 'Wine Blog', to: '/blog/wine' },
	{ name: 'WSET Study Blog', to: '/blog/study' },
	{ name: 'Contact', to: '/contact' },
	{ name: 'Privacy Policy', to: '/privacy-policy' },
	{ name: 'Sitemap', to: '/sitemap' },
	{ name: 'Algemene Voorwaarden', to: '/algemene-voorwaarden' },
]
type Item = {
	to: string
	name: string
}

const MenuItem = (props: { item: Item }) => (
	<li className="">
		<Link to={props.item.to}>{props.item.name}</Link>
	</li>
)

const NewsLetterSchema = z.object({
	name: NameSchema,
	email: EmailSchema,
	redirectTo: z.string().optional(),
	verified: z.boolean().optional(),
})

export function SignupEmail({
	onboardingUrl,
	otp,
}: {
	onboardingUrl: string
	otp: string
}) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>Welcome to Wine with Margaret!</E.Text>
				</h1>
				<p>
					<E.Text>
						Here's your verification code: <strong>{otp}</strong>
					</E.Text>
				</p>
				<p>
					<E.Text>Or click the link to get started:</E.Text>
				</p>
				<E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
			</E.Container>
		</E.Html>
	)
}

/*
 *	Let the newsletter signup post to a newsletter route, add name to input
 * Confirmation email is send, newsletter page contains 'go back' button
 * Confirmation code is entered in another page - email & otp
 *
 **/

export default function Footer() {
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'newsletter-signup',
		constraint: getZodConstraint(NewsLetterSchema),
		defaultValue: { redirectTo },
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: NewsLetterSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<footer className="col-span-1 w-full bg-black pb-10 pt-20 text-white">
			<div className="mx-auto px-6 lg:grid lg:max-w-7xl lg:grid-cols-footer lg:grid-rows-footer lg:gap-y-20">
				<div className="lg:col-start-1 lg:col-end-7 lg:grid lg:grid-cols-footer-links">
					<H4 className="mb-10 uppercase text-white lg:col-start-1 lg:col-end-3">
						@Wine.with.margaret
					</H4>
					<ul className="mb-10 grid grid-cols-footer-links gap-x-4 gap-y-10 uppercase lg:col-start-4 lg:col-end-7">
						{LINKS.map((item, i) => (
							<MenuItem item={item} key={`${i}+${item.name}`} />
						))}
					</ul>
				</div>
				<div className="newsletter-signup mb-10 lg:col-start-8 lg:col-end-13">
					<Link to="https://www.nix18.nl/" className="mb-4" target="_blank">
						<img src={nix18} alt="Nix18 Campagne logo" className="h-10" />
					</Link>
				</div>
				<div className="mb-10 flex gap-4 lg:row-start-2 lg:row-end-3">
					<IconLink target={SOCIALS.instagram}>
						<SiInstagram size={'24px'} />
					</IconLink>
					<IconLink target={SOCIALS.linkedin}>
						<SiLinkedin size={'24px'} />
					</IconLink>
					<IconLink target={SOCIALS.email}>
						<LuMail size={'24px'} />
					</IconLink>
				</div>
				<div className="lg:col-start-8 lg:col-end-13 lg:row-start-2 lg:row-end-3">
					<Paragraph className="text-sm" textColorClassName="color-primary">
						© {new Date().getFullYear()} - Wine with Margaret
					</Paragraph>
				</div>
			</div>
		</footer>
	)
}
